import { useState } from "react";
import { HeartPulse, Lock, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1584515933487-779824d29309?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxob3NwaWNlJTIwY2FyZSUyMGNvbXBhc3Npb25hdGUlMjBoYW5kc3xlbnwwfHx8fDE3ODc5NjY2MTB8MA&ixlib=rb-4.1.0&q=85";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div className="relative hidden w-1/2 lg:block" aria-hidden="true">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2C5545]/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-4xl font-semibold leading-tight">Compassionate care,<br />beautifully organized.</p>
          <p className="mt-3 max-w-md text-sm text-white/80">Scheduling for your hospice team — nurses, aides, social work, chaplaincy and more.</p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-[#FDFBF7] px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#2C5545] text-white">
              <HeartPulse className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-heading text-lg font-semibold text-[#1A1A1A]">Hospice Care</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">Staff Scheduler</p>
            </div>
          </div>

          <h1 className="font-heading text-4xl font-semibold tracking-tight text-[#1A1A1A]">Welcome back</h1>
          <p className="mt-2 text-sm text-[#5C6661]">Sign in with your administrator account.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="login-email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C6661]">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="login-email" type="email" required autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                  className="rounded-full border-[#E5E7EB] bg-white py-6 pl-10 focus-visible:ring-[#2C5545]"
                  data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="login-password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C6661]">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="login-password" type="password" required autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="rounded-full border-[#E5E7EB] bg-white py-6 pl-10 focus-visible:ring-[#2C5545]"
                  data-testid="login-password-input" />
              </div>
            </div>

            {error && (
              <p className="rounded-xl border border-[#FECDD3] bg-[#FFECE5] px-4 py-2.5 text-sm font-medium text-[#9F1239]" data-testid="login-error">{error}</p>
            )}

            <Button type="submit" disabled={busy}
              className="w-full rounded-full bg-[#2C5545] py-6 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#1E3B30]"
              data-testid="login-button">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
